import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useCart } from '../contexts/CartContext';
import { Product } from '../types';

interface AddToCartButtonProps {
  product: Product;
  className?: string;
}

const CartGlyph: React.FC<{ className?: string }> = ({ className = 'h-4 w-4' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const AddToCartButton: React.FC<AddToCartButtonProps> = ({ product, className = '' }) => {
  const { addToCart, toggleCart } = useCart();
  const [isAdding, setIsAdding] = useState(false);

  const handleAddToCart = () => {
    setIsAdding(true);
    addToCart({
      id: String(product.id),
      asin: product.asin || '',
      name: product.name,
      image: product.image,
      quantity: 1
    });
    setTimeout(() => {
      setIsAdding(false);
      toggleCart(true);
    }, 150);
  };

  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.95 }}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddToCart(); }}
      disabled={isAdding}
      aria-label={`Add ${product.name} to cart`}
      className={`btn btn-secondary inline-flex items-center justify-center font-medium px-3 py-2 sm:px-4 ${className} ${isAdding ? 'opacity-75 cursor-not-allowed' : 'shadow-md hover:shadow-lg transition-shadow'}`}
    >
      {isAdding ? (
        <>
          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="hidden sm:inline ml-2">Adding…</span>
        </>
      ) : (
        <>
          <CartGlyph />
          <span className="hidden sm:inline ml-2">Add to Cart</span>
          <span className="sm:hidden ml-1.5 text-sm">Add</span>
        </>
      )}
    </motion.button>
  );
};

export default AddToCartButton;
